import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";

// Reusable lead form. `kind` = contact | enquiry | quote | career
// `fields` selects which inputs to render.
export const LeadForm = ({ kind = "contact", presetMachine = "", presetService = "", compact = false }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { machine: presetMachine, service: presetService },
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await submitLead({ kind, ...values });
      toast.success("Thank you — your request has been received. Our team will respond shortly.");
      reset({ machine: presetMachine, service: presetService });
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#F5F5F7] border border-slate-200 rounded-xl px-4 py-3.5 text-black placeholder:text-slate-400 outline-none focus:border-[#0055FF] transition-colors";
  const labelCls = "block text-sm font-medium text-slate-600 mb-2";

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid={`lead-form-${kind}`}>
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-5`}>
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} placeholder="Your name" data-testid="lead-name"
            {...register("name", { required: true })} />
          {errors.name && <span className="text-xs text-red-400 mt-1 block">Name is required</span>}
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" className={inputCls} placeholder="you@company.com" data-testid="lead-email"
            {...register("email", { required: true })} />
          {errors.email && <span className="text-xs text-red-400 mt-1 block">Email is required</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Phone</label>
          <input className={inputCls} placeholder="+91 ..." data-testid="lead-phone" {...register("phone")} />
        </div>
        <div>
          <label className={labelCls}>Company</label>
          <input className={inputCls} placeholder="Company name" data-testid="lead-company" {...register("company")} />
        </div>
      </div>

      {kind === "enquiry" && (
        <div>
          <label className={labelCls}>Machine of Interest</label>
          <input className={inputCls} placeholder="e.g. VMC-850" data-testid="lead-machine" {...register("machine")} />
        </div>
      )}

      {kind === "quote" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Service / Machine</label>
            <input className={inputCls} placeholder="e.g. CNC Retrofitting" data-testid="lead-service" {...register("service")} />
          </div>
          <div>
            <label className={labelCls}>Estimated Budget</label>
            <input className={inputCls} placeholder="Optional" data-testid="lead-budget" {...register("budget")} />
          </div>
        </div>
      )}

      {kind === "career" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Position Applying For</label>
            <input className={inputCls} placeholder="Role" data-testid="lead-position" {...register("position")} />
          </div>
          <div>
            <label className={labelCls}>Years of Experience</label>
            <input className={inputCls} placeholder="e.g. 4 years" data-testid="lead-experience" {...register("experience")} />
          </div>
        </div>
      )}

      <div>
        <label className={labelCls}>{kind === "career" ? "Cover Note" : "Message"}</label>
        <textarea rows={4} className={inputCls} placeholder="Tell us about your requirement..." data-testid="lead-message"
          {...register("message")} />
      </div>

      <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="lead-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {loading ? "Sending..." : "Submit Request"}
      </button>
    </form>
  );
};

export default LeadForm;
